import { useEffect, useState } from 'react';
import TermsService from '../../service/terms/terms';
import ScrollToTopButton from '../components/ScrollToTopButton';
import './terms.css';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PageTop from '../components/pageTop/PageTop';
import Footer from '../components/footer/Footer';

interface ContentItem {
  seq: number;
  title: string;
  content:
    | {
        clause_idx: number;
        seq: number;
        title: string;
        content: {
          clause_idx: number;
          seq: number;
          title: string;
          content: any;
        }[];
      }[]
    | null;
  clause_idx: number;
}

interface Term {
  seq: number;
  title: string;
  content: ContentItem[];
  terms_idx: number;
}

interface ContentItem2 {
  seq: number;
  title: string;
  content: string | null;
  clause_idx: number;
}

interface Term2 {
  seq: number;
  title: string;
  content: ContentItem2[];
  terms_idx: number;
}

const cleanForDisplay = (text?: string) => {
  if (!text) return '';
  return (
    (text ?? '')
      .replace(/\u200B/g, '') // ✅ 제로폭문자 제거(복붙시 섞이는 경우)
      .replace(/\r\n?/g, '\n') // ✅ CRLF → LF 정규화
      .replace(/^[#'＃]+\s*/gm, '') // ✅ 줄 앞 #, ' 제거
      // 줄 시작의 **제목** 뒤 빈 줄들 1개로 축소(제목 바로 다음 줄에 본문)
      .replace(/(^|\n)\*\*(.+?)\*\*[ \t]*\n+/g, '$1<strong>$2</strong>\n')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  );
};

/** 프론트에서 직접 관리하는 최신 버전(2025-09-01) 식별자 */
const FRONT_LATEST_VERSION = -1 as const;
const FRONT_LATEST_DATE_LABEL = '2025-09-01';

/** 프론트 최신 버전 - 머리말(상단 소개문) */
const FRONT_INTRO = `트랜스파머(주)(이하 ‘트랜스파머’)는 이용자의 자유와 권리 보호를 위해 「개인정보 보호법」 및 관계 법령이 정한 바를 준수하여, 적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다. 이에 「개인정보 보호법」 제30조에 따라 이용자에게 개인정보 처리에 관한 절차 및 기준을 안내하고, 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을 수립·공개합니다.`;

/** 프론트 최신 버전 - 각 조항 본문(헤딩 줄은 빼고 내용만) */
const FRONT_SECTIONS: Record<number, string> = {
  1: `트랜스파머는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.

회원 가입 및 관리
회원 가입의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증, 회원자격 유지·관리, 서비스 부정이용 방지, 만 14세 이상 확인, 각종 고지·통지, 고충처리, 보안정책 수립 목적으로 개인정보를 처리합니다.

**재화 또는 서비스 제공**
콘텐츠 제공, 신규 서비스 개발 및 맞춤 서비스 제공, 서비스 품질 확인 및 개선, 만족도 및 설문 조사, 본인인증, 연령인증, 신규서비스 이용 시 또는 본인인증 시 보유정보를 이용한 입력편의성 제공, 청구서 발송, 요금 결제·정산, 경매·공매 물건 등록 및 관리, 물건 분석 서비스 제공, 경매·공매 관련 법령상 의무이행 등을 목적으로 개인정보를 처리합니다.

**마케팅 및 광고에의 활용**
신규 서비스(제품) 개발 및 맞춤 서비스 제공, 이벤트 및 광고성 정보 제공 및 참여기회 제공, 상품 및 경품의 배송, 이벤트 당첨자 선정, 인구통계학적 특성과 이용자의 관심 및 성향의 추정을 통한 서비스 제공 및 광고 게재, 서비스의 유효성 확인, 접속빈도 파악 또는 회원의 서비스 이용에 대한 통계를 목적으로 개인정보를 처리합니다. 또한, 고객 동의 시 제휴 마케팅 대행사(예: ㈜엠딕)에 이벤트 및 광고성 정보 제공을 위한 최소한의 개인정보를 제공할 수 있습니다.

**챗봇 서비스 운영**
이용자가 챗봇과 대화하는 과정에서 직접 입력하는 개인정보(성명, 연락처, 이메일, 서비스 문의 내용 등)는 문의 응대, 서비스 품질 개선, 고객 지원 기록 관리의 목적으로만 처리되며, 민감정보 입력은 금지됩니다. 입력한 경우 해당 정보는 문의 처리 범위 내에서만 활용됩니다.

트랜스파머는 이용자의 개인정보를 가명화하여 관련 법령에서 정한 바에 따라 처리할 수 있습니다. 또한, 수집 목적과 합리적 관련성이 있는 범위에서 관련 법령에 따라 이용자의 추가적인 동의 없이 이용자의 개인정보를 이용하거나 제공할 수 있습니다. 나아가 이용자의 개인정보를 모두 익명처리한 정보의 집합물을 관련 법령에 따라 제공하거나 처리할 수 있습니다.`,
  2: `트랜스파머는 다음의 개인정보 항목을 처리하고 있습니다.

**회원 가입 및 관리**
● 필수항목 : 성명, 생년월, 휴대폰 번호, 이메일 주소, 서비스 사용 목적과 관련된 정보(법인명, 직함), 통신사, 위치정보(위치정보서비스이용약관 동의에 따르며 별도의 동의 절차에 따라 처리됨)
● 선택항목 : SNS (카카오, 네이버) 로그인 이용 시, 로그인 정보 식별 값, SNS 프로필 사진

**재화 또는 서비스 제공**
● 필수항목 : 성명, 생년월, 휴대폰 번호, 이메일 주소, 별명
● 선택항목 : 서비스 사용 목적을 위해 이용자가 직접 입력하는 정보(경매·공매 물건 분석 정보, 투자 계획 정보, NPL 분석 정보 등)

**경매·공매 물건 분석 서비스 이용**
● 필수항목
‣ 일반회원 : 이름, 연락처, 관심 지역 정보
‣ 물건분석: 물건 소재지, 물건 정보(면적, 감정가, 최저가, 용도 등)
● 선택항목
‣ 물건 사진, 상세 설명, 특이사항

**챗봇 서비스 이용**
● 필수항목 : 대화 과정에서 이용자가 직접 입력하는 성명, 연락처, 이메일 등 (입력 시에 한함)
● 선택항목 : 서비스 문의, 투자 관련 질문, 기타 이용자가 자발적으로 입력하는 정보

**자동 생성 정보**
● IP 주소, 쿠키, 접속 로그, 서비스 이용 기록, 기기정보

**마케팅 및 광고에의 활용**
● 필수항목 : 성명, 생년월, 휴대폰 번호, 이메일주소, 서비스 이용과정에서 자동수집되는 정보
● 선택항목 : 마케팅 정보 제공 및 제휴 프로모션 참여를 위한 제3자 제공 동의 항목 (성명, 휴대폰 번호, 이메일 주소)`,
  3: `트랜스파머는 법령에 따른 개인정보 보유·이용기간 또는 이용자로부터 개인정보를 수집 시에 동의 받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.

각각의 개인정보 처리 및 보유 기간은 다음과 같습니다.

**서비스 이용 관련 정보**
1. 회원가입 및 관리: 회원 탈퇴 시까지
2. 재화 또는 서비스 제공 : 회원 탈퇴 시까지
3. 민원 사무 처리 : 회원 탈퇴 시까지
4. 마케팅 및 광고에의 활용 : 회원 탈퇴 시까지

**관련 법령에 따른 정보보유**
1. 전자상거래 등에서의 소비자 보호에 관한 법률에 따름
● 계약 또는 청약철회 등에 관한 기록: 5년
● 대금결제 및 재화 등의 공급에 관한 기록: 5년
● 소비자의 불만 또는 분쟁처리에 관한 기록: 3년
● 표시·광고에 관한 기록: 6개월
2. 부동산 거래신고 등에 관한 법률에 따름
● 거래신고 관련 자료: 5년
3. 「통신비밀보호법」에 따른 통신사실확인자료 보관
● 가입자 전기통신일시, 개시·종료시간, 상대방 가입자번호, 사용도수, 발신기지국 위치 추적자료 : 1년
● 컴퓨터통신, 인터넷 로그기록자료, 접속지 추적자료 : 3개월`,
  4: `트랜스파머는 이용자의 별도 동의가 있거나 법령에 규정된 경우를 제외하고는 이용자의 개인정보를 제3자에게 제공하지 않습니다.

1. 법령상 제공이 필요한 경우
● 제공받는 자: 국토교통부, 관할 지방자치단체
● 제공목적: 부동산 거래신고 등 관련 법령상 의무 이행
● 제공항목: 거래당사자 정보(이름, 주소, 연락처 등)
● 보유기간: 관련 법령에 따른 보존기간

1. [선택 동의] 마케팅 목적 제3자 제공
트랜스파머는 이용자의 사전 동의 하에 아래와 같이 개인정보를 제3자에게 제공할 수 있습니다.
    - 제공받는 자: ㈜엠딕
    - 제공 목적: 이벤트 및 프로모션 마케팅 업무 대행
    - 제공 항목: 성명, 휴대폰 번호, 이메일 주소
    - 보유 및 이용 기간: 제공일로부터 1년 또는 목적 달성 시까지
    ※ 해당 항목은 이용자의 선택 동의가 필요한 항목이며, 동의하지 않더라도 기본 서비스 이용에는 제한이 없습니다.`,
  5: `트랜스파머는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.

1. 파기절차
● 이용자가 입력한 정보는 목적 달성 후 별도의 DB에 옮겨져(종이의 경우 별도의 서류) 내부 방침 및 기타 관련 법령에 따라 일정기간 저장된 후 혹은 즉시 파기됩니다.
● 이 때, DB로 옮겨진 개인정보는 법률에 의한 경우가 아니고서는 다른 목적으로 이용되지 않습니다.
2. 파기방법
● 전자적 파일 형태의 정보는 기록을 재생할 수 없는 기술적 방법을 사용하여 삭제합니다.
● 종이에 출력된 개인정보는 분쇄기로 분쇄하거나 소각을 통하여 파기합니다.`,
  6: `트랜스파머는 1년간 서비스를 이용하지 않은 이용자는 휴면계정으로 전환하고, 개인정보를 별도로 분리하여 보관합니다. 분리 보관된 개인정보는 1년간 보관 후 지체없이 파기합니다.
트랜스파머는 휴면전환 30일 전까지 휴면예정 회원에게 별도 분리 보관되는 사실 및 휴면예정일, 별도 분리 보관하는 개인정보 항목을 이메일, 문자 등 이용자에게 통지 가능한 방법으로 알리고 있습니다.
휴면계정으로 전환을 원하지 않으시는 경우, 휴면계정 전환 전 서비스 로그인을 하시면 됩니다. 또한, 휴면계정으로 전환되었더라도 로그인을 하는 경우 이용자의 동의에 따라 휴면계정을 복원하여 정상적인 서비스를 이용할 수 있습니다.`,
  7: `이용자는 트랜스파머에 대해 언제든지 개인정보 열람·정정·삭제·처리정지 요구 등의 권리를 행사할 수 있습니다.

1. 개인정보 열람요구
● 회사에서 보유하고 있는 개인정보파일은 「개인정보 보호법」 제35조(개인정보의 열람)에 따라 열람을 요구할 수 있습니다.
● 다만, 개인정보 열람 요구는 「개인정보 보호법」 제35조 제4항에 의하여 다음과 같이 제한될 수 있습니다.
    ● 법률에 따라 열람이 금지되거나 제한되는 경우
    ● 다른 사람의 생명·신체를 해할 우려가 있거나 다른 사람의 재산과 그 밖의 이익을 부당하게 침해할 우려가 있는 경우
    ● 공공기관이 다음 각 목의 어느 하나에 해당하는 업무를 수행할 때 중대한 지장을 초래하는 경우
2. 개인정보 정정·삭제 요구
● 회사가 보유하고 있는 개인정보에 대해서는 「개인정보 보호법」 제36조(개인정보의 정정·삭제)에 따라 개인정보의 정정·삭제를 요구할 수 있습니다.
● 다만, 다른 법령에서 그 개인정보가 수집 대상으로 명시되어 있는 경우에는 그 삭제를 요구할 수 없습니다.
3. 권리행사 방법
● 개인정보 열람, 정정·삭제, 처리정지 요구는 회사 웹사이트의 '개인정보 수정' 메뉴, 고객센터, 전자우편, 서면 등을 통하여 하실 수 있습니다.
● 이용자가 개인정보의 오류에 대한 정정을 요청한 경우에는 정정을 완료하기 전까지 당해 개인정보를 이용 또는 제공하지 않습니다.
● 잘못된 개인정보를 제3자에게 이미 제공한 경우에는 정정 처리결과를 제3자에게 지체없이 통지하여 정정이 이루어지도록 하겠습니다.
4. 법정대리인의 권리행사
● 만 14세 미만 아동에 관한 개인정보의 열람 등 요구는 법정대리인이 직접 해야 합니다.
● 만 14세 이상의 미성년자인 이용자는 본인이 직접 권리를 행사하거나 법정대리인을 통하여 권리를 행사할 수 있습니다.`,
  8: `트랜스파머는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.
- 개인정보 보호를 위한 내부관리계획 수립 및 시행
- 개인정보 접근권한의 차등 부여 및 관리
- 정기적인 보안점검 및 교육 실시
- 해킹 방지를 위한 방화벽 및 백신 프로그램 설치
- 개인정보 암호화 저장 및 전송 시 보안 강화
- 챗봇 서비스 대화 내용 암호화 저장·관리, 개인정보 입력 시 안내 문구를 통해 사전 고지`,
  9: `트랜스파머는 이용자에게 개별적인 맞춤서비스를 제공하기 위해 이용 정보를 저장하고 수시로 불러오는 ‘쿠키(cookie)’를 사용합니다.

1. 쿠키 등 사용 목적
● 회원과 비회원의 접속 빈도나 방문 시간 등을 분석
● 이용자의 취향과 관심분야를 파악하여 타겟 마케팅 및 서비스 개선에 활용
● 서비스 이용 기록을 통한 개인 맞춤 서비스 제공
2. 쿠키 설정 거부 방법
이용자는 쿠키 설치에 대한 선택권을 가지고 있습니다. 따라서 웹브라우저에서 옵션을 설정함으로써 모든 쿠키를 허용하거나, 쿠키가 저장될 때마다 확인을 거치거나, 아니면 모든 쿠키의 저장을 거부할 수도 있습니다.
● 설정방법 예시 (Internet Explorer의 경우): 웹 브라우저 상단의 도구 > 인터넷 옵션 > 개인정보 > 설정`,
  10: `트랜스파머는 통계작성, 과학적 연구, 공익적 기록보존 등을 위하여 필요한 경우 수집한 개인정보를 특정 개인을 알아볼 수 없도록 가명 처리할 수 있습니다.`,
  11: `트랜스파머는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 이용자의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
● 개인정보 보호책임자 성명 : 박성필
● 직책 : CIO
● 연락처 : sp.park@transfarmer.co.kr

이용자는 「개인정보 보호법」 제35조에 따른 개인정보의 열람 청구를 아래의 부서에 할 수 있습니다. 트랜스파머는 이용자의 개인정보 열람청구가 신속하게 처리되도록 노력하겠습니다.

‣ 대표번호: 02)555-2822
‣ 이메일: biz@transfarmer.co.kr`,
  12: `개인정보침해에 대한 신고나 상담이 필요한 경우에는 아래 기관에 문의하시기 바랍니다.
● 개인정보침해신고센터 (privacy.kisa.or.kr / 국번없이 118)
● 대검찰청 사이버수사과 (www.spo.go.kr / 국번없이 1301)
● 경찰청 사이버수사국 (ecrm.cyber.go.kr / 국번없이 182)
● 개인정보분쟁조정위원회 (www.kopico.go.kr / 국번없이 1833-6972)

「개인정보 보호법」 제35조(개인정보의 열람), 제36조(개인정보의 정정·삭제), 제37조(개인정보의 처리정지 등)의 규정에 의한 요구에 대하여 공공기관의 장이 행한 처분 또는 부작위로 인하여 권리 또는 이익의 침해를 받은 자는 행정심판법이 정하는 바에 따라 행정심판을 청구할 수 있습니다.
● 중앙행정심판위원회 : (국번없이) 110 / www.simpan.go.kr`,
  13: `이 개인정보 처리방침은 2025년 09월 01일부터 적용됩니다.`,
};

/** Term[] 구조로 변환 */
const buildFrontLatestTerms = (): Term[] => {
  const items: ContentItem[] = Object.keys(FRONT_SECTIONS)
    .map(Number)
    .sort((a, b) => a - b)
    .map((seq) => ({
      clause_idx: -1000 - seq,
      seq,
      title: FRONT_SECTIONS[seq],
      content: null,
    }));
  return [
    {
      terms_idx: FRONT_LATEST_VERSION,
      seq: 1,
      title: '최신(프론트) 개인정보처리방침',
      content: items,
    },
  ];
};

const PrivacyTerms = ({ SNS, setTermsOpen }: any) => {
  const [verisionList, setVersionList] = useState<
    { version: 0; create_date: '' }[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<{
    version: number;
    create_date: string;
  } | null>(null);

  const [terms, setTerms] = useState<Term[]>([]);
  const [addendum, setAddendum] = useState<Term2[]>([]);

  const navigate = useNavigate();

  const getDateList = async () => {
    try {
      const res = await TermsService.version('P');
      const serverList = res.result || [];
      const merged = [
        { version: FRONT_LATEST_VERSION, create_date: FRONT_LATEST_DATE_LABEL },
        ...serverList,
      ];
      setVersionList(merged);
      if (!selectedDate) setSelectedDate(merged[0]); // 기본 선택: 프론트 최신
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getDateList();
  }, []);

  const getTerms = async (version: number) => {
    try {
      if (version === FRONT_LATEST_VERSION) {
        setAddendum([]);
        setTerms(buildFrontLatestTerms());
        return;
      }
      const res = await TermsService.terms(version, 'P');
      setAddendum(res.result.addendum);
      setTerms(res.result.list);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      getTerms(selectedDate.version);
    } else if (verisionList.length > 0) {
      getTerms(verisionList[0].version);
    }
  }, [selectedDate, verisionList]);

  const dummy = [
    { name: '1. 개인정보처리방침의 목적', href: '#tag1' },
    { name: '2. 개인정보 수집 항목', href: '#tag2' },
    { name: '3. 개인정보의 처리 및 보유 기간 ', href: '#tag3' },
    { name: '4. 개인정보의 제3자 제공에 관한 사항', href: '#tag4' },
    { name: '5. 개인정보의 파기 절차 및 방법에 관한 사항', href: '#tag5' },
    { name: '6. 미이용자의 개인정보 파기 등에 관한 조치', href: '#tag6' },
    { name: '7. 이용자와 법정대리인의 권리·의무 및 행사방법 ', href: '#tag7' },
    { name: '8. 개인정보의 안전성 확보조치에 관한 사항', href: '#tag8' },
    {
      name: '9. 개인정보를 자동으로 수집하는 장치의 설치·운영 및 그 거부에 관한 사항',
      href: '#tag9',
    },
    { name: '10. 가명정보 처리에 관한 사항  ', href: '#tag10' },
    { name: '11. 개인정보 보호책임자와 민원처리 부서', href: '#tag11' },
    { name: '12. 이용자의 권익침해에 대한 구제방법', href: '#tag12' },
  ];

  const renderTitle = (seq: number) => {
    switch (seq) {
      case 1:
        return '개인정보처리방침의 목적';
      case 2:
        return '개인정보 수집 항목';
      case 3:
        return '개인정보의 처리 및 보유 기간';
      case 4:
        return '개인정보의 제3자 제공에 관한 사항';
      case 5:
        return '개인정보의 파기 절차 및 방법에 관한 사항';
      case 6:
        return '미이용자의 개인정보 파기 등에 관한 조치';
      case 7:
        return '이용자와 법정대리인의 권리·의무 및 행사방법';
      case 8:
        return '개인정보의 안전성 확보조치에 관한 사항';
      case 9:
        return '개인정보를 자동으로 수집하는 장치의 설치·운영 및 그 거부에 관한 사항';
      case 10:
        return '가명정보 처리에 관한 사항';
      case 11:
        return '개인정보 보호책임자와 민원처리 부서';
      case 12:
        return '이용자의 권익침해에 대한 구제방법';
      default:
        return '';
    }
  };

  return (
    <Container id="termsContainer">
      <ScrollToTopButton />
      <PageTop text="이용약관" />
      <div className="wrap">
        <div className="terms-inner">
          <div className="terms-title">
            <span>개인정보처리방침</span>
            <div className="version-select">
              <select
                className="select-non-border"
                name=""
                id=""
                onChange={(e) => {
                  const selectedVersion = verisionList.find(
                    (v) => v.version === Number(e.target.value),
                  );
                  if (selectedVersion) {
                    setSelectedDate(selectedVersion);
                  }
                }}
                value={selectedDate?.version || ''}
              >
                {verisionList.map((item) => (
                  <option key={item.version} value={item.version}>
                    {item.create_date}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="terms-content-box">
            <div className="terms-privacy-script">
              트랜스파머(주)(이하 ‘트랜스파머’)는 이용자의 자유와 권리 보호를
              위해 「개인정보 보호법」 및 관계 법령이 정한 바를 준수하여,
              적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다. 이에
              「개인정보 보호법」 제30조에 따라 이용자에게 개인정보 처리에 관한
              절차 및 기준을 안내하고, 이와 관련한 고충을 신속하고 원활하게
              처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을
              수립·공개합니다.{' '}
            </div>
            <div className="terms-anchor-list">
              {dummy.map((item: any, idx: number) => {
                return (
                  <a
                    key={idx}
                    className="terms-content-item-text pointer"
                    href={item.href}
                    style={{ textDecoration: 'none', color: 'black' }}
                  >
                    {item.name}
                  </a>
                );
              })}
            </div>
            {terms.map((term) => (
              <div
                key={term.terms_idx}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '32px',
                }}
              >
                {term.content.map((item) => (
                  <div key={item.clause_idx} className="terms-content-item">
                    <div
                      id={`tag${item.seq}`}
                      className="terms-content-item-title"
                    >
                      {item.seq !== 13 ? `${item.seq}.` : ''}{' '}
                      {renderTitle(item.seq)}
                    </div>
                    <div
                      className="terms-content-item-text"
                      dangerouslySetInnerHTML={{
                        __html: cleanForDisplay(item.title),
                      }}
                    ></div>
                    {item.content &&
                      item.content.map((content) => (
                        <div
                          key={content.clause_idx}
                          style={{ fontSize: '13px', marginLeft: '4px' }}
                        >
                          {content.seq}
                          {')'} {content.title}
                          {content.content &&
                            content.content.map((c, idx) => (
                              <div key={idx}>- {c.title}</div>
                            ))}
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            ))}
            <div className="divider" />
            <HomeButton>
              <div
                className="home-button"
                onClick={() => {
                  navigate('/');
                }}
              >
                홈으로
              </div>
            </HomeButton>
          </div>
        </div>
      </div>
      <Footer />
    </Container>
  );
};

export default PrivacyTerms;

const Container = styled.div`
  // width: 1200px;
  // margin: 0 auto;
  width: 100%;
  height: 100%; /* 화면 높이만큼 설정 */
  overflow-y: auto; /* 세로 스크롤 가능하게 설정 */
  margin: 0 auto;
  background-color: #fff;
`;

const HomeButton = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
